import React, { useContext } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AppProvider, AppContext } from './context/AppContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Footer from './components/common/Footer';
import Alert from './components/common/Alert';
import { ROUTES, ROLES } from './utils/constants';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import PropertiesPage from './pages/PropertiesPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationDetail from './components/application/ApplicationDetail';
import ApplicantPortalPage from './pages/ApplicantPortalPage';
import ApplicantApplicationPage from './pages/ApplicantApplicationPage';
import InviteLinkPage from './pages/InviteLinkPage';
import ApplyToPropertyPage from './pages/ApplyToPropertyPage';
import MyDataPage from './pages/MyDataPage';
import PropertyApplicantsPage from './pages/PropertyApplicantsPage';
import GetStartedPage from './pages/GetStartedPage';
import OrganisationPage from './pages/OrganisationPage';
import OrganisationInvitationsPage from './pages/OrganisationInvitationsPage';
import MandateReviewsPage from './pages/MandateReviewsPage';
import PricingPage from './pages/PricingPage';
import FaqPage from './pages/FaqPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NotFoundPage from './pages/NotFoundPage';
import ConsentPage from './pages/ConsentPage';
import VerificationPage from './pages/VerificationPage';
import ReferenceResponsePage from './pages/ReferenceResponsePage';
import AuditLogPage from './pages/AuditLogPage';
import VerificationTask from './components/verification/VerificationTask';
import TenantPassportPage from './pages/TenantPassportPage';
import DisputesPage from './pages/DisputesPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import ReportPage from './pages/ReportPage';
import DecisionPage from './pages/DecisionPage';

import './App.css';

const Layout = () => {
  const { notifications, removeNotification } = useContext(AppContext);

  return (
    <div className="app-container">
      <Header />
      <div className="page-layout">
        <Sidebar />
        <main className="content-area">
          <div className="notifications-container">
            {notifications.map(notif => (
              <Alert 
                key={notif.id} 
                type={notif.type} 
                message={notif.message} 
                dismissible 
                onClose={() => removeNotification(notif.id)} 
              />
            ))}
          </div>
          <Outlet />
        </main>
      </div>
      <Footer />
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppProvider>
        <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.HOME} element={<LandingPage />} />
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
          <Route path={ROUTES.APPLY_INVITE} element={<InviteLinkPage />} />
          <Route path={ROUTES.APPLY_PROPERTY} element={<ApplyToPropertyPage />} />
          <Route path={ROUTES.REFERENCE_RESPONSE} element={<ReferenceResponsePage />} />
          
          {/* Authenticated Layout */}
          <Route element={<Layout />}>
            <Route path={ROUTES.DASHBOARD} element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path={`${ROUTES.PROPERTIES}/*`} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT]}>
                <PropertiesPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.APPLICATIONS} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT]}>
                <ApplicationsPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.APPLICATION_DETAIL} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT, ROLES.VERIFIER]}>
                <ApplicationDetail 
                  application={{ id: '1', firstName: 'John', lastName: 'Doe', propertyAddress: '123 Main St', status: 'SUBMITTED' }} 
                  consents={[]} 
                  documents={[]} 
                />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.APPLICANT_PORTAL} element={
              <ProtectedRoute allowedRoles={[ROLES.APPLICANT]}>
                <ApplicantPortalPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.APPLICANT_APPLICATION} element={
              <ProtectedRoute allowedRoles={[ROLES.APPLICANT]}>
                <ApplicantApplicationPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.MY_DATA} element={
              <ProtectedRoute allowedRoles={[ROLES.APPLICANT]}>
                <MyDataPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.ORGANISATION} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT, ROLES.VERIFIER, ROLES.READONLY]}>
                <OrganisationPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.ORGANISATION_INVITATIONS} element={
              <ProtectedRoute>
                <OrganisationInvitationsPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.ADMIN_MANDATES} element={
              <ProtectedRoute platformAdmin>
                <MandateReviewsPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.GET_STARTED} element={
              <ProtectedRoute>
                <GetStartedPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PROPERTY_APPLICANTS} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT]}>
                <PropertyApplicantsPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.CONSENT_PAGE} element={
              <ProtectedRoute allowedRoles={[ROLES.APPLICANT]}>
                <ConsentPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.VERIFICATION} element={
              <ProtectedRoute allowedRoles={[ROLES.VERIFIER, ROLES.ADMIN, ROLES.OWNER]}>
                <VerificationPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.VERIFICATION_TASK} element={
              <ProtectedRoute allowedRoles={[ROLES.VERIFIER, ROLES.ADMIN]}>
                <VerificationTask />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.AUDIT} element={
              <ProtectedRoute allowedRoles={[ROLES.COMPLIANCE_ADMIN, ROLES.ADMIN]}>
                <AuditLogPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.PASSPORT} element={
              <ProtectedRoute allowedRoles={[ROLES.APPLICANT]}>
                <TenantPassportPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.DISPUTES} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN]}>
                <DisputesPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.BILLING} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN]}>
                <BillingPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.ADMIN} element={
              <ProtectedRoute allowedRoles={[ROLES.COMPLIANCE_ADMIN, ROLES.ADMIN]}>
                <AdminPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.REPORT} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT, ROLES.VERIFIER]}>
                <ReportPage />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.DECISION} element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER, ROLES.ADMIN, ROLES.AGENT]}>
                <DecisionPage />
              </ProtectedRoute>
            } />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppProvider>
    </AuthProvider>
  );
};

export default App;
